import React, { useState } from 'react'
import { clientDatabasesAPI } from '@hpanel/web-common/api/client'

/**
 * Modal for rotating either the primary user's password or a non-primary
 * DB user's password. The two endpoints are nearly identical; we route
 * via the `target` discriminator so the same form serves both.
 *
 * §9 contract: API failures surface fetchAPI's clean message verbatim,
 * including the server's `detail` (e.g. "Password too weak"). We never
 * echo the new password back into the error, never store it in URL/
 * query, and clear it from React state after a successful rotation.
 */

export type RotateTarget =
  | { kind: 'primary'; dbId: number }
  | { kind: 'user'; dbId: number; dbUserId: number }

export interface RotatePasswordModalProps {
  target: RotateTarget
  onClose: () => void
  onRotated: () => void
  /** Display label shown in the modal title (e.g. "alice_owner"). */
  username?: string
}

export const RotatePasswordModal: React.FC<RotatePasswordModalProps> = ({
  target,
  onClose,
  onRotated,
  username,
}) => {
  const [pw, setPw] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Server enforces min 12 — we pre-validate so users get instant
  // feedback rather than a network round-trip and a 422.
  const tooShort = pw.length > 0 && pw.length < 12
  const mismatch = confirm.length > 0 && pw !== confirm
  const canSubmit =
    !busy && pw.length >= 12 && pw === confirm && !tooShort && !mismatch

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    try {
      if (target.kind === 'primary') {
        await clientDatabasesAPI.rotatePrimaryPassword(target.dbId, {
          new_password: pw,
        })
      } else {
        await clientDatabasesAPI.rotateUserPassword(
          target.dbId,
          target.dbUserId,
          { new_password: pw },
        )
      }
      // Clear sensitive state before signalling success.
      setPw('')
      setConfirm('')
      onRotated()
      onClose()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[RotatePasswordModal] rotation failed', e)
      setError(e instanceof Error ? e.message : 'Password rotation failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      data-testid="rotate-password-modal"
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Rotate Password
        </h2>
        {username && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            For <span className="font-mono">{username}</span>
          </p>
        )}

        {error && (
          <div
            data-testid="rotate-error"
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-sm text-red-700 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              New Password
            </label>
            <input
              type="password"
              required
              autoComplete="new-password"
              minLength={12}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="new-password"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="At least 12 characters"
            />
            {tooShort && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                Minimum 12 characters.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="confirm-password"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Repeat the new password"
            />
            {mismatch && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                Passwords do not match.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="rotate-cancel"
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="rotate-submit"
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Rotating…' : 'Rotate Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
